import React, { useState } from 'react'
import axios from 'axios'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getAccessToken, setAccessToken } from './AuthStore.ts'
import styles from './SignUpForm.module.css'
import logoIcon from '../../assets/logos/logo.png'

const SignUpPage = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const location = useLocation()
  const navigate = useNavigate()
  const isOAuthSignup = location.state?.fromOAuth === true
  const startStep = location.state?.startStep || 1

  const [signupAccessToken, setSignupAccessToken] = useState(null)
  const [emailCheckResult, setEmailCheckResult] = useState(false)
  const [step, setStep] = useState(startStep) 
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    nickname: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    gender: '',
    interests: []
  })

  const handleSubmit = async () => {
    const birthMonth = String(formData.birthMonth).padStart(2, '0')
    const birthDay = String(formData.birthDay).padStart(2, '0')

    // OAuth 회원가입인 경우 email과 password 제외
    const userData = isOAuthSignup
      ? {
          username: formData.name,
          nickname: formData.nickname,
          birthDate: `${formData.birthYear}-${birthMonth}-${birthDay}`,
          gender: formData.gender.toUpperCase(),  
          hobbies: formData.interests
        }
      : {
          email: formData.email,
          password: formData.password,
          username: formData.name,
          nickname: formData.nickname,
          birthDate: `${formData.birthYear}-${birthMonth}-${birthDay}`,
          gender: formData.gender.toUpperCase(),  
          hobbies: formData.interests
        };

    console.log(userData);
  
    try {
      let response;
      
      if (isOAuthSignup) {
        // OAuth 회원가입
        response = await axios.post(
          `${import.meta.env.VITE_API_BASE}/api/user/signup/extra`,
          userData,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`
            }
          }
        );
      } else {
        // 일반 회원가입
        response = await axios.post(
          `${import.meta.env.VITE_API_BASE}/api/user/signup`,
          userData
        );
      }
  
      if (response.status === 200) {

        console.log(response.data);
        
        const accessToken = response.data?.data?.accessToken || response.data?.accessToken

        if (accessToken) {
          setSignupAccessToken(accessToken)
        }
        setStep(4)
      }
      else if(response.status === 409){
        console.error('회원가입 실패:', error);
        alert('이미 존재하는 사용자입니다.');
      }
    } catch (error) {
      console.error('회원가입 실패:', error);
      alert('회원가입에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleEmailCheck = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/user/check-username`,
        {
          params: {
            username : formData.email
          }
        }
      )

      const isAvailable =
        response?.data?.data?.isAvailable ?? response?.data?.isAvailable

      if (isAvailable === true) {
        alert('사용 가능한 이메일입니다.')
        setEmailCheckResult(true)
      } else {
        alert('이미 사용 중인 이메일입니다.')
        setEmailCheckResult(false)
      }
    } catch (error) {
      console.error('이메일 중복 확인 실패:', error)
      alert('이메일 중복 확인 중 오류가 발생했습니다.')
      setEmailCheckResult(false)
    }
  }

  const handleInputChange = (field, value) => {
    if (field === 'email') {
      setEmailCheckResult(false)
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleInterestToggle = (interest) => {
    setFormData(prev => {
      const newInterests = prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest];

      return {
        ...prev,
        interests: newInterests
      };
    });
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1)
    }
  }

  const handlePrev = () => {
    if (isOAuthSignup && step === 2) {
      return
    }
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      default:
        return renderStep1()
    }
  }

  // Step 1: 이메일 및 비밀번호
  const renderStep1 = () => {
    const isEmailValid = emailPattern.test(formData.email)

    return (
      <div className={styles.stepContent}>
      <div className={styles.header}>
        <h2>Draw&You 계정 생성</h2>
        <p>당신만의 그림 이야기를 시작해보세요</p>
      </div>

      <div className={styles.divider}>
      </div>

      <div className={styles.form}>
        <div className={styles.field}>
          <label>이름</label>
          <input
            type="text"
            placeholder="이름을 입력해주세요"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>이메일</label>
          <div className={styles.emailRow}>
            <input
              type="email"
              placeholder="이메일을 입력해주세요"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`${!isEmailValid && formData.email ? styles.error : ''} ${styles.emailInput}`}
              disabled={emailCheckResult}
            />
            <button
              type="button"
              className={styles.emailCheckBtn}
              disabled={!formData.email || !isEmailValid || emailCheckResult}
              onClick={handleEmailCheck}
            >
              {emailCheckResult ? '사용 가능' : '중복 확인'}
            </button>
          </div>
          {!isEmailValid && formData.email && (
            <span className={styles.errorMessage}>올바른 이메일 형식을 입력해주세요</span>
          )}
        </div>

        {/*
        <div className={styles.field}>
          <label>인증번호</label>
          <div className={styles.verificationRow}>
            <input
              type="text"
              placeholder="인증번호"
              className={styles.verificationInput}
              value={formData.verificationCode}
              onChange={(e) => handleInputChange('verificationCode', e.target.value)}
            />
            <button type="button" className={styles.verificationBtn}>
              인증
            </button>
            <button type="button" className={styles.verificationBtn}>
              재전송
            </button>
          </div>
        </div> 
        */}

        <div className={styles.field}>
          <label>비밀번호</label>
          <input
            type="password"
            placeholder="비밀번호를 입력해주세요"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>비밀번호 확인</label>
          <input
            type="password"
            placeholder="비밀번호를 다시 입력해주세요"
            value={formData.passwordConfirm}
            onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
            className={formData.passwordConfirm && formData.password !== formData.passwordConfirm ? styles.error : ''}
          />
          {formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
            <span className={styles.errorMessage}>비밀번호가 일치하지 않습니다</span>
          )}
        </div>

        <div className={styles.actions}>
          <Link to="/" className={styles.prevBtn}>
            취소
          </Link>
          <button 
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={
              !formData.name ||
              !formData.email ||
              !isEmailValid ||
              !emailCheckResult ||
              !formData.password ||
              !formData.passwordConfirm ||
              formData.password !== formData.passwordConfirm
            }
          >
            계속
          </button>
        </div>
      </div>
    </div>
    )
  }

  // Step 2: 개인정보 입력
  const renderStep2 = () => (
    <div className={styles.stepContent}>
      <div className={styles.header}>
        <h2>Draw&You 계정 생성</h2>
        <p>당신은 어떤 사람인가요?</p>
      </div>

      <div className={styles.divider}>
      </div>

      <div className={styles.form}>
        <div className={styles.field}>
          <label>닉네임</label>
          <input
            type="text"
            placeholder="닉네임을 입력해주세요"
            value={formData.nickname}
            onChange={(e) => handleInputChange('nickname', e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>생년월일</label>
          <div className={styles.birthDateRow}>
            <select 
              value={formData.birthYear}
              onChange={(e) => handleInputChange('birthYear', e.target.value)}
            >
              <option value="">연도</option>
              {Array.from({ length: 100 }, (_, i) => {
                const year = new Date().getFullYear() - i
                return <option key={year} value={year}>{year}</option>
              })}
            </select>
            <select 
              value={formData.birthMonth}
              onChange={(e) => handleInputChange('birthMonth', e.target.value)}
            >
              <option value="">월</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}월</option>
              ))}
            </select>
            <select 
              value={formData.birthDay}
              onChange={(e) => handleInputChange('birthDay', e.target.value)}
            >
              <option value="">일</option>
              {Array.from({ length: 31 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}일</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.field}>
          <label>성별</label>
          <select 
            value={formData.gender}
            onChange={(e) => handleInputChange('gender', e.target.value)}
          >
            <option value="">성별</option>
            <option value="male">남성</option>
            <option value="female">여성</option>
          </select>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.prevBtn} 
            onClick={handlePrev}
            disabled={isOAuthSignup}
          >
            이전
          </button>
          <button 
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={!formData.nickname || !formData.birthYear || !formData.birthMonth || !formData.birthDay || !formData.gender}
          >
            계속
          </button>
        </div>
      </div>
    </div>
  )

  // Step 3: 관심사 선택
  const renderStep3 = () => {
    const interestOptions = [
      { id: 'MUSIC', label: '음악' },
      { id: 'MOVIE_DRAMA', label: '영화 / 드라마' },
      { id: 'BOOK_WRITING', label: '책 / 글쓰기' },
      { id: 'TRAVEL', label: '여행' },
      { id: 'FITNESS', label: '운동 / 피트니스' },
      { id: 'ART_DESIGN', label: '미술 / 디자인' },
      { id: 'GAME', label: '게임' },
      { id: 'FOOD_COOKING', label: '음식 / 요리' },
      { id: 'NATURE_ANIMAL', label: '자연 / 동물' },
      { id: 'FASHION_STYLE', label: '패션 / 스타일' }
    ]

    return (
      <div className={styles.stepContent}>
        <div className={styles.header}>
          <h2>Draw&You 계정 생성</h2>
          <p>여정을 위한 마지막 단계</p>
        </div>

        <div className={styles.divider}>
        </div>

        <div className={styles.form}>
          <div className={styles.field}>
            <label>관심사 (1개 이상 선택)</label>
            <div className={styles.interestGrid}>
              {interestOptions.map((interest) => (
                <button
                  key={interest.id}
                  className={`${styles.interestBtn} ${
                    formData.interests.includes(interest.id) ? styles.selected : ''
                  }`}
                  onClick={() => handleInterestToggle(interest.id)}
                >
                  {interest.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.actions}>
            <button className={styles.prevBtn} onClick={handlePrev}>
              이전
            </button>
            <button 
              className={styles.nextBtn}
              onClick={handleSubmit}
              disabled={formData.interests.length === 0}
            >
              가입
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 4: 완료
  const handleLogin = () => {
    if (signupAccessToken) {
      setAccessToken(signupAccessToken)
      navigate('/dashboard')
    } else {
      navigate('/')
    }
  }

  const renderStep4 = () => (
    <div className={styles.stepContent}>
      <div className={styles.completionScreen}>
        <div className={styles.completionHeader}>
          <h2>Draw&You 계정 생성</h2>
        </div>
        
        <div className={styles.completionSpacer}></div>
        
        <div className={styles.completionContent}>
          <h2>🎉 계정 생성 완료!</h2>
          <p>이제 당신만의 그림 이야기를 시작해보세요.<br />환영합니다!</p>
        </div>
        
        <button onClick={handleLogin} className={styles.startBtn}>
          지금 로그인하기
        </button>
      </div>
    </div>
  )

  return (
    <div className={styles.signUpPage}>
      
      <div className={styles.pageChrome}>
        <Link to="/" className={styles.logo}>
          <img src={logoIcon} alt="Draw&You" />
        </Link>

        <div className={styles.pageProgress}>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>
      </div>
      
      <div className={styles.container}>
        {renderStepContent()}
      </div>
    </div>
  )
}

export default SignUpPage